import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import _ from 'lodash';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAssignUserToPatient } from '@hooks/api/vitall-patient';
import { useSnackbar } from '@hooks/useSnackbar';

import { formatName } from '@utils/format';
import { hasRole } from '@utils/role';

import { RoleName } from 'generated/types';
import { useGetAllUsersLazyQuery } from 'generated/user.generated';

const messages = defineMessages({
  label: {
    id: 'assignedUser.label',
    defaultMessage: 'Assigned User',
  },
  curator: {
    id: 'assignedUser.label.curator',
    defaultMessage: 'Assigned Curator',
  },
  practitioner: {
    id: 'assignedUser.label.practitioner',
    defaultMessage: 'Assigned Practitioner',
  },
  error: {
    id: 'assignedUser.error',
    defaultMessage: 'Failed to assign user to patient',
  },
  select: {
    id: 'GLOBAL.select',
    defaultMessage: 'Select',
  },
});

interface AssignedUserDropdownProps {
  name?: string | null;
  value?: string | null;
  vitall_patient_id?: string | null;
  role: RoleName;
  account_id?: string | null;
}

export default function AssignedUserDropdown({
  account_id,
  name,
  value,
  vitall_patient_id,
  role,
}: AssignedUserDropdownProps) {
  const [labelId] = useState(_.uniqueId('assigned-user-'));

  const [open, setOpen] = useState<boolean>(false);
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const label =
    role === RoleName.Curator
      ? messages.curator
      : role === RoleName.Practitioner
        ? messages.practitioner
        : messages.label;

  const [getAllUsers, { data: usersData, loading: usersLoading }] =
    useGetAllUsersLazyQuery();
  const [assignUserToPatient, assignUserToPatientLoading] =
    useAssignUserToPatient();
  const loading = usersLoading || assignUserToPatientLoading;

  const userOptions = (usersData?.getAllUsers ?? [])
    .filter((user) => hasRole(user, role))
    .sort((a, b) => a.first_name.localeCompare(b.first_name))
    .reduce<Record<string, string>>(
      (acc, { first_name, last_name, user_id }) => {
        acc[user_id] = formatName(first_name, last_name) ?? '-';
        return acc;
      },
      {}
    );

  const handleChange = async (user_id: string) => {
    try {
      if (!vitall_patient_id) throw new Error('No vitall patient');
      if (value === user_id) {
        return;
      }
      await assignUserToPatient({
        input: {
          vitall_patient_id,
          user_id,
        },
      });
    } catch (e) {
      enqueueSnackbar({
        message: formatMessage(messages.error),
        variant: 'error',
      });
    }
  };

  const handleOpen = async (event: React.SyntheticEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setOpen(true);

    await getAllUsers({
      variables: {
        input: {
          account_id,
          roles: [role],
        },
      },
    });
  };

  const handleClose = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setOpen(false);
  };

  return (
    <Autocomplete
      disableClearable
      disabled={loading}
      id={labelId}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      isOptionEqualToValue={(option, value) => option === value}
      getOptionDisabled={(option) => option === value}
      getOptionLabel={(user_id) => {
        if (user_id in userOptions) {
          return userOptions[user_id];
        }
        return name ?? '';
      }}
      onChange={(event, value) => {
        event.stopPropagation();
        event.preventDefault();
        if (value) {
          handleChange(value);
        }
      }}
      options={Object.keys(userOptions)}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          disabled={loading}
          label={formatMessage(label)}
          placeholder={formatMessage(messages.select)}
          onClick={(event) => {
            event.stopPropagation();
          }}
          slotProps={{
            input: {
              ...params.InputProps,
              onClick: (e) => e.stopPropagation(),
              onFocus: (e) => e.stopPropagation(),
              endAdornment: (
                <React.Fragment>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            },
            inputLabel: { shrink: true },
          }}
          sx={({ palette, shadows }) => ({
            '& .MuiInputBase-root': {
              backgroundColor: palette.background.paper,
              boxShadow: shadows[1],
              fontSize: 14,
              ':hover': {
                cursor: 'pointer',
              },
            },
          })}
        />
      )}
      renderOption={(props, user_id) => {
        const userName =
          user_id in userOptions ? userOptions[user_id] : (name ?? '');
        return (
          <li {...props} key={`user-${user_id}`}>
            {userName}
          </li>
        );
      }}
      size="small"
      sx={{
        width: 'fit-content',
        minWidth: 160,
        '.MuiAutocomplete-input': {
          cursor: 'pointer',
          ':hover': {
            cursor: 'pointer',
          },
        },
      }}
      value={value ?? ''}
      ListboxProps={{ onClick: (e) => e.stopPropagation() }}
    />
  );
}
